import React, { useEffect, useRef, useState } from "react";
import { Box, Tooltip, IconButton, Divider, Checkbox, Typography } from "@mui/material";
import { ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon, CenterFocusWeak as CenterFocusWeakIcon, Undo as UndoIcon, Redo as RedoIcon, DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
          ThreeSixty as ThreeSixtyIcon, SwapHoriz as SwapHorizIcon, SwapVert as SwapVertIcon, ContentCut as ContentCutIcon, ContentCopy as ContentCopyIcon, BorderAll as BorderAllIcon,
          ContentPaste as ContentPasteIcon, SquareOutlined as SquareOutlinedIcon, Brightness4, Brightness7, Save as SaveIcon, Rule as RuleIcon,
          AccountTree as AccountTreeIcon } from "@mui/icons-material";
import "./GraphToolbar.css";
import { mxConstants, mxUtils, mxEvent } from "../../utils/graph/mxModule";
import StrokeWidth from "../EdgeStyle/StrokeWidth";
import EdgeColorSelector from "../ColorPallete/EdgeColorSelector";
import { ClickAwayListener } from "@mui/material";
import { VIEW_TYPES } from "../../constants/Constants";

const GraphToolbar = (props) => {
const { graph, containerRef, commonRef, isDarkMode, toggleMode, isSelectionEmpty, hasClipboardContent, handleSave, 
        saveEnabled, checkAndSave, handleEdgeColorChange, handleEdgeWidthChange, handleOutlineVisible,
        onShowNetlist, onValidate , diagramMode } = props;

  const [wireMode, setWireMode] = useState(false);
  const [gridEnabled, setGridEnabled] = useState(true);
  const [pageBreaksVisible, setPageBreaksVisible] = useState(false);
  const [outlineVisible, setOutlineVisible] = useState(true);
  const [validatedConstraints, setValidatedConstraints] = useState([]);
  const [isMultipleSelection, setIsMultipleSelection] = useState(false);

  const originalHandlersRef = useRef({ isStartEvent: null, mouseUp: null });

  useEffect(() => {
    if (!graph || !containerRef || !graph.connectionHandler ) return;
    
    const handler = graph.connectionHandler;
    // Cache original handlers
if (!handler || typeof handler.isStartEvent !== 'function' || typeof handler.mouseUp !== 'function') return;
    if (!originalHandlersRef.current.isStartEvent)
      originalHandlersRef.current.isStartEvent = handler.isStartEvent;
    if (!originalHandlersRef.current.mouseUp)
      originalHandlersRef.current.mouseUp = handler.mouseUp;

    // Restore originals before overwriting
    handler.isStartEvent = originalHandlersRef.current.isStartEvent;
    handler.mouseUp = originalHandlersRef.current.mouseUp;

    // Wire Mode override
    handler.isStartEvent = function (me) {
      return (
        wireMode ||
        originalHandlersRef.current.isStartEvent.apply(this, arguments)
      );
    };

    handler.mouseUp = function (sender, me) {
      if (this.first != null && this.previous != null) {
        const point = mxUtils.convertPoint(
          this.graph.container,
          me.getX(),
          me.getY()
        );
        const dx = Math.abs(point.x - this.first.x);
        const dy = Math.abs(point.y - this.first.y);

        if (dx < this.graph.tolerance && dy < this.graph.tolerance) {
          if (!wireMode && this.graph.getModel().isEdge(this.previous.cell)) {
            this.reset();
          }
          return;
        }
      }

      originalHandlersRef.current.mouseUp.apply(this, arguments);
    };

    // Edge style
    const style = graph.getStylesheet().getDefaultEdgeStyle();
    style[mxConstants.STYLE_EDGE] = "wireEdgeStyle";

    // Grid background
    const updateGrid = () => {
    if (gridEnabled) {
      const gridSize = 10 * graph.view.scale; // scale grid with zoom
      const gridLineColor = isDarkMode
        ? "rgba(255, 255, 255, 0.1)"
        : "rgba(0, 0, 0, 0.1)";
      containerRef.style.backgroundImage = `
        linear-gradient(to right, ${gridLineColor} 1px, transparent 1px),
        linear-gradient(to bottom, ${gridLineColor} 1px, transparent 1px)
      `;
      containerRef.style.backgroundSize = `${gridSize}px ${gridSize}px`;
    } else {
      containerRef.style.background = "";
    }
    }
    
    // initial draw
    updateGrid();

    // wrap validateBackground
    const oldValidate = graph.view.validateBackground;
    graph.view.validateBackground = function () {
      oldValidate.apply(this, arguments);
      updateGrid();
    };
  }, [wireMode, gridEnabled, graph, isDarkMode, containerRef]);

  useEffect(() => {
    if (!graph) return;

    setGridEnabled(commonRef.getGridEnabled());
    setOutlineVisible(commonRef.getOutlineVisible());
    setWireMode(commonRef.getWireMode());
    setPageBreaksVisible(commonRef.getPageBreaksVisible());

    // Keyboard shortcut handler
    const handleKeyDown = (e) => {
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement.tagName === "INPUT" ||
        activeElement.tagName === "TEXTAREA" ||
        activeElement.isContentEditable;

      if (isInputFocused) return; // Don't process shortcut keys inside inputs

      const key = e.key.toLowerCase();

      switch (key) {
        case "u":
          if (e.shiftKey) {
            e.preventDefault();
            commonRef.handleRedo();
          } else {
            e.preventDefault();
            commonRef.handleUndo();
          }
          break;
        case "]":
          e.preventDefault();
          commonRef.zoomIn();
          break;
        case "[":
          e.preventDefault();
          commonRef.zoomOut();
          break;
        case "f":
          e.preventDefault();
          commonRef.zoomFit();
          break;
        case "delete":
          e.preventDefault();
          commonRef.delete();
          break;
        case "a":
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            commonRef.selectAll();
          }
          break;
          case "s":
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            handleSave();
          }
          break;
        default:
          break;
      }
    };
    commonRef.registerKeyHandlers();

    window.addEventListener("keydown", handleKeyDown);

    // Cleanup listeners
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      commonRef.disposeKeyHandlers();
    };
  }, [graph, handleSave]);

  // Track selection changes to detect multiple selections
  useEffect(() => {
    if (!graph) return;

    const selectionListener = () => {
      const selectedCells = graph.getSelectionCells();
      setIsMultipleSelection(selectedCells.length > 1);
    };

    graph.getSelectionModel().addListener(mxEvent.CHANGE, selectionListener);

    return () => {
      graph.getSelectionModel().removeListener(selectionListener);
    };
  }, [graph]);

  const handleGridToggle = () => {
    setGridEnabled(commonRef.toggleGrid());
  };

  const handleOutLine = () => {
    const newValue = commonRef.toggleOutline();
    setOutlineVisible(newValue);
    handleOutlineVisible(newValue);
  };

  const handleWireModeToggle = () => {
    setWireMode(commonRef.toggleWireMode());
  };

  const handleToggleGridLines = () => {
    setPageBreaksVisible(commonRef.togglePageBreaks());
  };

  const handleValidation = (e) => {
    e.stopPropagation();
    onValidate();
  };

  return (
      <Box className="controller-container">
      {(graph.view_type_id === VIEW_TYPES.stick) && (
        <Box style={{backgroundColor:"#e0e0e0",display:"flex", borderRadius:"6px"}}>
          <Tooltip title="Wire Color">
            <Box sx={{ padding: "5px 0px" }}>
              <EdgeColorSelector onColorChange={handleEdgeColorChange} />
            </Box>
          </Tooltip>
          <Divider orientation="vertical" flexItem />
          {/* <Tooltip title="Wire Stroke Width"> */}
            <Box>
              <StrokeWidth onWidthChange={handleEdgeWidthChange} />
            </Box>
          {/* </Tooltip> */}
        </Box>
      )}
      {(graph.view_type_id === VIEW_TYPES.stick) && (
        <Tooltip title="Stick to Schematic Validation">
          <Box sx={{ position: "relative", display: "inline-block", marginLeft: 1 }}>
            <IconButton onClick={handleValidation} disabled={saveEnabled}>
              <RuleIcon sx={{fontSize:'medium'}}/>
            </IconButton>
            <Typography>Validate</Typography>
          </Box>
        </Tooltip>)}
      {!(graph.view_type_id === VIEW_TYPES.symbol) && (
        <Tooltip title="View Netlist">
          <Box>
            <IconButton onClick={onShowNetlist} disabled={saveEnabled}>
              <AccountTreeIcon sx={{ fontSize: 'medium' }} />
            </IconButton>
            <Typography>Netlist</Typography>
          </Box>
        </Tooltip>
      )}
        <Tooltip title="Ctrl+S">
          <Box>
          <IconButton onClick={() =>  handleSave()} disabled={!saveEnabled} >
            <SaveIcon sx={{fontSize:'medium'}}/>
          </IconButton>
           <Typography>Save</Typography>
          </Box>
        </Tooltip>
        <Divider orientation="vertical" flexItem />
        <Tooltip title="Toggle Dark/Light Mode">
          <Box>
          <IconButton 
          onClick={toggleMode}
          >
            {isDarkMode ? <Brightness7 sx={{fontSize:'medium'}}/> : <Brightness4 sx={{fontSize:'medium'}}/>}
          </IconButton>
           <Typography>Mode</Typography>
          </Box>
        </Tooltip>
        <Divider orientation="vertical" flexItem />
        <Tooltip title="Zoom In ( ] )">
          <Box>
          <IconButton onClick={() => commonRef.zoomIn()}>
            <ZoomInIcon sx={{fontSize:'medium'}}/>
          </IconButton>
           <Typography>In +</Typography>
          </Box>
        </Tooltip>
        <Tooltip title="Zoom Out ( [ )">
          <Box>
          <IconButton
            onClick={() => commonRef.zoomOut()}
          >
            <ZoomOutIcon sx={{fontSize:'medium'}}/>
          </IconButton>
           <Typography>Out -</Typography>
          </Box>
        </Tooltip>
        <Tooltip title="Zoom Fit (F)">
          <Box>
          <IconButton
            onClick={() => commonRef.zoomFit()}
          >
            <CenterFocusWeakIcon sx={{fontSize:'medium'}}/>
          </IconButton>
          <Typography>Fit</Typography>
          </Box>
        </Tooltip>
        <Divider orientation="vertical" flexItem />
        <Tooltip title="Undo (U)">
          <Box>
          <IconButton
            onClick={() => commonRef.handleUndo()}
          >
            <UndoIcon sx={{fontSize:'medium'}}/>
          </IconButton>
          <Typography>Undo</Typography>
          </Box>
        </Tooltip>
        <Tooltip title="Redo (Shift + U)">
          <Box>
          <IconButton
            onClick={() => commonRef.handleRedo()}
          >
            <RedoIcon sx={{fontSize:'medium'}}/>
          </IconButton>
          <Typography>Redo</Typography>
          </Box>
        </Tooltip>
        <Divider orientation="vertical" flexItem />
        {!(graph.view_type_id === VIEW_TYPES.symbol) && (
          <>
        {/* <Tooltip title="Rotate">
          <Box>
          <IconButton onClick={() => commonRef.rotate()}>
            <ThreeSixtyIcon sx={{fontSize:'medium'}}/>
          </IconButton>
           <Typography>Rotate</Typography>
          </Box>
        </Tooltip> */}
        <Tooltip title={isMultipleSelection ? "You can flip only one component at a time!" : "Flip Horizontal"}>
          <Box>
          <IconButton
            onClick={() => commonRef.flipHorizontal(diagramMode)}
            disabled={isMultipleSelection}
          >
            <SwapHorizIcon sx={{fontSize:'medium'}}/>
          </IconButton>
           <Typography>Flip H</Typography>
          </Box>
        </Tooltip>
        <Tooltip title={isMultipleSelection ? "You can flip only one component at a time!" : "Flip Vertical"}>
          <Box>
          <IconButton
            onClick={() => commonRef.flipVertical()}
            disabled={isMultipleSelection}
          >
            <SwapVertIcon sx={{fontSize:'medium'}}/>
          </IconButton>
           <Typography>Flip V</Typography>
          </Box>
        </Tooltip>
        </>
        )}
        <Divider orientation="vertical" flexItem />
         {!(graph.view_type_id === VIEW_TYPES.symbol) && (
         <>
        <Tooltip title="Ctrl+X">
        <Box>
          <IconButton onClick={() => commonRef.cut()} disabled={isSelectionEmpty}>
            <ContentCutIcon sx={{fontSize:'medium'}}/>
          </IconButton>
            <Typography>Cut</Typography>
        </Box>
        </Tooltip>
        <Tooltip title="Ctrl+C">
          <Box>
          <IconButton onClick={() => commonRef.copy()} disabled={isSelectionEmpty}>
            <ContentCopyIcon sx={{fontSize:'medium'}}/>
          </IconButton>
          <Typography>Copy</Typography>
          </Box>
        </Tooltip>
        <Tooltip title="Ctrl+V">
          <Box>
          <IconButton onClick={() => commonRef.paste()} disabled={!hasClipboardContent}>
            <ContentPasteIcon sx={{fontSize:'medium'}}/>
          </IconButton>
          <Typography>Paste</Typography>
          </Box>
        </Tooltip>
         <Tooltip title="Delete (Del)">
          <Box>
          <IconButton onClick={() => commonRef.delete()} disabled={isSelectionEmpty}>
            <DeleteOutlineOutlinedIcon sx={{fontSize:'medium'}}/>
          </IconButton>
           <Typography>Delete</Typography>
          </Box>
        </Tooltip>
        </>)}
        <Divider orientation="vertical" flexItem />
        <Tooltip title="Toggle Grid Lines">
         <Box>
          <IconButton onClick={() => handleToggleGridLines()}>
            {pageBreaksVisible ? <BorderAllIcon sx={{fontSize:'medium'}}/> : <SquareOutlinedIcon sx={{fontSize:'medium'}}/>}
          </IconButton>
           <Typography>Lines</Typography>
          </Box>
        </Tooltip>
        <Tooltip title="Toggle Grid">
          <Box>
            <Checkbox
              checked={gridEnabled}
              onChange={handleGridToggle}
              sx={{'& .MuiSvgIcon-root': { fontSize: 'medium'} }}
            />
            <Typography>Grid</Typography>
          </Box>
        </Tooltip>
        {!(graph.view_type_id === VIEW_TYPES.symbol) && (
          <>
        <Tooltip title="Toggle Outline">
          <Box>
            <Checkbox
              checked={outlineVisible}
              onChange={() => handleOutLine()}
              sx={{ "& .MuiSvgIcon-root": { fontSize: "medium" } }}
            />
            <Typography>Outline</Typography>
          </Box>
        </Tooltip>
        {/* <Divider orientation="vertical" flexItem />
        <Tooltip title="Toggle Wire Mode">
          <Box>
            <Checkbox
              checked={wireMode}
              onChange={handleWireModeToggle}
              sx={{'& .MuiSvgIcon-root': { fontSize: 'medium'} }}
            />
            <Typography>Wire Mode</Typography>
          </Box>
        </Tooltip> */}
        </>
        )}
        
      </Box>
  );
};

export default GraphToolbar;